import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const HEARTBEAT_MS = 25000;
const STORAGE_KEY = 'airmax_visitor_id';

type ClientIdentity = {
  clientName?: string;
  clientPhoneHint?: string;
  clientContext?: string;
  supportAction?: string;
};

type AirmaxVisitorApi = {
  identify: (info: ClientIdentity) => void;
  note: (action: string) => void;
  flush: () => void;
};

declare global {
  interface Window {
    AirmaxVisitor?: AirmaxVisitorApi;
  }
}

function makeVisitorId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID().replace(/-/g, '').slice(0, 32);
    }
  } catch {
    /* ignore */
  }
  return `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
}

function getVisitorId(): string {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing && existing.length >= 8) {
      return existing;
    }
    const id = makeVisitorId();
    localStorage.setItem(STORAGE_KEY, id);
    return id;
  } catch {
    return makeVisitorId();
  }
}

function readIdentity(): ClientIdentity {
  try {
    const raw = sessionStorage.getItem('airmax_visitor_identity');
    if (!raw) return {};
    const parsed = JSON.parse(raw) as ClientIdentity;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeIdentity(info: ClientIdentity): void {
  try {
    const next = { ...readIdentity(), ...info };
    sessionStorage.setItem('airmax_visitor_identity', JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function buildPayload(path: string, extra: ClientIdentity = {}): Record<string, string> {
  const identity = { ...readIdentity(), ...extra };
  const screen =
    typeof window !== 'undefined'
      ? `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}`
      : '';
  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    timezone = '';
  }
  return {
    visitorId: getVisitorId(),
    path,
    title: typeof document !== 'undefined' ? document.title || '' : '',
    referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
    language: typeof navigator !== 'undefined' ? navigator.language || '' : '',
    timezone,
    screen,
    clientName: identity.clientName || '',
    clientPhoneHint: identity.clientPhoneHint || '',
    clientContext: identity.clientContext || '',
    supportAction: identity.supportAction || '',
  };
}

async function sendHeartbeat(path: string, extra: ClientIdentity = {}): Promise<void> {
  const payload = buildPayload(path, extra);
  try {
    await fetch('/api/visitor-track.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'same-origin',
      keepalive: true,
      cache: 'no-store',
    });
  } catch {
    /* non-blocking */
  }
}

/**
 * Anonymous presence heartbeat for admin live visitors.
 * Also exposes window.AirmaxVisitor for client pages to attach name/phone.
 */
export default function VisitorTracker() {
  const location = useLocation();

  useEffect(() => {
    const api: AirmaxVisitorApi = {
      identify: (info) => {
        writeIdentity(info);
        void sendHeartbeat(window.location.pathname + window.location.search, info);
      },
      note: (action) => {
        writeIdentity({ supportAction: action });
        void sendHeartbeat(window.location.pathname + window.location.search, {
          supportAction: action,
        });
      },
      flush: () => {
        void sendHeartbeat(window.location.pathname + window.location.search);
      },
    };
    window.AirmaxVisitor = api;
    return () => {
      if (window.AirmaxVisitor === api) {
        delete window.AirmaxVisitor;
      }
    };
  }, []);

  useEffect(() => {
    const path = location.pathname + location.search;
    void sendHeartbeat(path);

    const interval = window.setInterval(() => {
      void sendHeartbeat(path);
    }, HEARTBEAT_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void sendHeartbeat(path);
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [location.pathname, location.search]);

  return null;
}
